import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from 'react-modal';
import Swal from 'sweetalert2';
import { FiEdit, FiTrash, FiPlus } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';


function formatDateFr(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('T')[0].split('-');
  return `${d}/${m}/${y}`;
}

const emptyAbsence = (user) => ({
  inscription_id: '',
  periode_id: '',
  date_absence: new Date().toISOString().slice(0, 10),
  journee_entiere: 'N',
  incident_id: '',
  motif: '',
  dmd_constat: 'Constaté',
  user_id: user?.id || ''
});

const AbsencesPage = () => {
  const { user } = useAuth();
  const [absences, setAbsences] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyAbsence(user));
  const [search, setSearch] = useState('');
  const [incidents, setIncidents] = useState([]);
  const [periodes, setPeriodes] = useState([]);
  const [inscriptions, setInscriptions] = useState([]);

  useEffect(() => {
    fetchAbsences();
    api.get('/incidents').then(res => setIncidents(res.data.data || []));
    api.get('/periodes').then(res => setPeriodes(res.data.data || []));
    api.get('/inscriptions').then(res => setInscriptions(res.data.data || []));
  }, []);

  const fetchAbsences = async () => {
    const res = await api.get('/absences');
    setAbsences(res.data.data || []);
  };

  const handleAdd = () => {
    setEditing(null);
    setForm(emptyAbsence(user));
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditing(item);
    setForm({
      inscription_id: item.inscription_id || '',
      periode_id: item.periode_id || '',
      date_absence: item.date_absence || new Date().toISOString().slice(0, 10),
      journee_entiere: item.journee_entiere || 'N',
      incident_id: item.incident_id || '',
      motif: item.motif || '',
      dmd_constat: item.dmd_constat || 'Constaté',
      user_id: user?.id || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Supprimer cette absence ?',
      text: 'Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    });
    if (result.isConfirmed) {
      await api.delete(`/absences/${id}`);
      fetchAbsences();
      Swal.fire('Supprimé !', 'L\'absence a été supprimée.', 'success');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditing(null);
    setForm(emptyAbsence(user));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]: type === 'checkbox' ? (checked ? 'Y' : 'N') : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.inscription_id || !form.periode_id || !form.date_absence) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: 'Les champs obligatoires doivent être remplis.' });
      return;
    }
    const dataToSend = { ...form, user_id: user?.id };
    if (editing) {
      await api.put(`/absences/${editing.id}`, dataToSend);
    } else {
      await api.post('/absences', dataToSend);
    }
    fetchAbsences();
    setShowModal(false);
  };

  // Pagination locale
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const filteredAbsences = absences.filter(a =>
    (a.motif?.toLowerCase().includes(search.toLowerCase()) ||
     a.dmd_constat?.toLowerCase().includes(search.toLowerCase()) ||
     String(a.date_absence).includes(search) ||
     String(a.UserId).includes(search))
  );
  const totalPages = Math.max(1, Math.ceil(filteredAbsences.length / pageSize));
  const paginatedAbsences = filteredAbsences.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Absences</h1>
        <button
          className="flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          onClick={handleAdd}
        >
          <FiPlus />
          <span>Nouvelle absence</span>
        </button>
      </div>
      {/* Formulaire de recherche */}
      <div className="bg-white shadow rounded-lg overflow-hidden mb-4 p-4">
        <input
          type="text"
          placeholder="Recherche par motif, date, utilisateur..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="border rounded px-3 py-2 w-full"
        />
      </div>
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3">Élève</th>
              <th className="px-6 py-3">Période</th>
              <th className="px-6 py-3">Date</th>
              <th className="px-6 py-3">Journée entière</th>
              <th className="px-6 py-3">Incident</th>
              <th className="px-6 py-3">Motif</th>
              <th className="px-6 py-3">Constat</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredAbsences.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-4 text-center text-gray-500">Aucune absence</td>
              </tr>
            ) : (
              paginatedAbsences.map((item) => (
                <tr key={item.id}>
                  <td className="px-6 py-4">
                    {inscriptions.find(i => i.id === item.inscription_id)?.nom || item.inscription_id}
                  </td>
                  <td className="px-6 py-4">
                    {periodes.find(p => p.id === item.periode_id)?.periode || item.periode_id}
                  </td>
                  <td className="px-6 py-4">{formatDateFr(item.date_absence)}</td>
                  <td className="px-6 py-4">{item.journee_entiere === 'Y' ? 'Oui' : 'Non'}</td>
                  <td className="px-6 py-4">
                    {incidents.find(i => i.id === item.incident_id)?.designation || ''}
                  </td>
                  <td className="px-6 py-4">{item.motif}</td>
                  <td className="px-6 py-4">{item.dmd_constat}</td>
                  <td className="px-6 py-4 text-right">
                    <button
                      className="p-2 text-primary-600 hover:text-primary-900 mr-2 rounded-full"
                      onClick={() => handleEdit(item)}
                      title="Modifier"
                    >
                      <FiEdit size={18} />
                    </button>
                    <button
                      className="p-2 text-red-600 hover:text-red-900 rounded-full"
                      onClick={() => handleDelete(item.id)}
                      title="Supprimer"
                    >
                      <FiTrash size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Pagination locale harmonisée */}
      {!showModal && totalPages > 1 && (
        <div className="flex justify-center items-center gap-1 mt-4 flex-wrap pb-4">
          <button onClick={() => setPage(1)} disabled={page === 1} className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-sm">Premier</button>
          <button onClick={() => setPage(page - 1)} disabled={page === 1} className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-sm">Précédent</button>
          {(() => {
            const pages = [];
            if (totalPages <= 10) {
              for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
              }
            } else {
              for (let i = 1; i <= 5; i++) pages.push(i);
              if (page > 7 && page < totalPages - 6) {
                pages.push('...');
                for (let i = page - 1; i <= page + 1; i++) pages.push(i);
                pages.push('...');
              } else {
                pages.push('...');
              }
              for (let i = totalPages - 4; i <= totalPages; i++) {
                if (i > 5) pages.push(i);
              }
            }
            return [...new Set(pages)].map((p, idx) =>
              p === '...'
                ? <span key={"ellipsis-" + idx} className="mx-0.5 px-2">...</span>
                : <button
                    key={p}
                    className={`px-2 py-1 rounded mx-0.5 text-sm ${p === page ? 'bg-primary-600 text-white' : 'bg-gray-100 hover:bg-primary-100 text-gray-700'}`}
                    onClick={() => setPage(p)}
                    disabled={p === page}
                  >{p}</button>
            );
          })()}
          <button onClick={() => setPage(page + 1)} disabled={page === totalPages} className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-sm">Suivant</button>
          <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-sm">Dernier</button>
        </div>
      )}

      {/* Modal ajout/mise à jour */}
      <Modal
        isOpen={showModal}
        onRequestClose={handleModalClose}
        shouldCloseOnOverlayClick={false}
        contentLabel="Absence"
        ariaHideApp={false}
        style={{
          content: {
            maxWidth: '800px',
            width: '100%',
            margin: 'auto',
            borderRadius: 12,
            padding: 0,
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            transform: 'translate(-50%, -50%)',
          }
        }}
      >
        <div className="flex justify-end p-2">
        <button
            type="button"
            onClick={handleModalClose}
            className="text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none"
            title="Fermer"
            style={{height: 36, minWidth: 36, lineHeight: '36px', borderRadius: '50%'}}
        >×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6">
          <h2 className="text-lg font-semibold mb-4">{editing ? 'Modifier' : 'Nouvelle'} absence</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Élève *</label>
              <select
                name="inscription_id"
                value={form.inscription_id}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2"
                required
              >
                <option value="">Sélectionner...</option>
                {inscriptions.map(i => (
                  <option key={i.id} value={i.id}>{i.nom} {i.prenom}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Période *</label>
              <select
                name="periode_id"
                value={form.periode_id}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2"
                required
              >
                <option value="">Sélectionner...</option>
                {periodes.map(p => (
                  <option key={p.id} value={p.id}>{p.periode}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Date *</label>
              <input
                type="date"
                name="date_absence"
                value={form.date_absence}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
            <div className="flex items-center mt-6">
              <input
                type="checkbox"
                id="journee_entiere"
                name="journee_entiere"
                checked={form.journee_entiere === 'Y'}
                onChange={handleChange}
                className="mr-2"
              />
              <label htmlFor="journee_entiere" className="text-sm font-medium">Journée entière</label>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Incident associé</label>
              <select
                name="incident_id"
                value={form.incident_id}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2"
              >
                <option value="">Aucun</option>
                {incidents.map(i => (
                  <option key={i.id} value={i.id}>{i.designation}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Motif</label>
              <input
                type="text"
                name="motif"
                value={form.motif}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Constat</label>
              <input
                type="text"
                name="dmd_constat"
                value={form.dmd_constat}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button type="button" onClick={handleModalClose} className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300">Annuler</button>
            <button type="submit" className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700">{editing ? 'Enregistrer' : 'Créer'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AbsencesPage;
